import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDatabase, FiFileText, FiShield } from 'react-icons/fi';
import './LegalPage.css';

const LAST_UPDATED = 'August 22, 2026';

const policies = {
  privacy: {
    eyebrow: 'Your information',
    title: 'Privacy Policy',
    icon: FiShield,
    intro: 'This policy explains how personal information is handled when students and instructors use MedSim, the AI Patient Simulator.',
    sections: [
      {
        title: '1. Scope and operator',
        paragraphs: [
          'This policy applies to MedSim accounts, simulated patient encounters, assessments, instructor review, and related support services.',
          'The university, clinic, or other organization that deploys and administers your MedSim instance is the personal-data operator. Contact that organization’s administrator or designated privacy contact for deployment-specific details. This policy is intended to be read with any additional notice supplied by that organization.'
        ]
      },
      {
        title: '2. Information we collect',
        bullets: [
          'Account information: name, email address, optional phone number, date of birth, institution or place of study, account role, and internal user identifier.',
          'Training information: selected cases, chat transcripts, requested clinical findings, submitted diagnoses and management plans, automated scores, feedback, and instructor reviews.',
          'Technical information: authentication and session data, language and interface preferences, service logs, usage records, and information needed to secure and operate the platform.',
          'Case-authoring information supplied by authorized instructors.'
        ]
      },
      {
        title: '3. Why we process information',
        bullets: [
          'To create and secure accounts and provide role-based access.',
          'To run simulated clinical encounters and generate educational feedback.',
          'To save progress, results, transcripts, and instructor assessments.',
          'To maintain, troubleshoot, protect, and improve the platform.',
          'To comply with applicable educational, data-protection, security, and record-keeping obligations.'
        ]
      },
      {
        title: '4. Legal basis and Uzbekistan law',
        paragraphs: [
          'Depending on the deployment, processing may be based on your consent, performance of the service agreement, the operating institution’s educational responsibilities, legitimate platform-security needs, or a legal obligation.',
          'Deployments in Uzbekistan should be administered in accordance with applicable law, including the Law of the Republic of Uzbekistan “On Personal Data” No. ZRU-547 and applicable localization, registration, consent, and security requirements. The operating organization is responsible for confirming the requirements that apply to its deployment.'
        ]
      },
      {
        title: '5. AI processing and service providers',
        paragraphs: [
          'MedSim uses configured AI providers to generate simulated patient responses, clinical-system replies, and assessment feedback. Relevant case facts, conversation text, diagnosis, and management-plan content may be sent to those providers for processing.',
          'Do not enter real patient names, identifiers, records, or other confidential clinical information. MedSim cases are educational simulations and should use fictional or properly de-identified information only. Hosting, database, infrastructure, and AI providers may process limited information on behalf of the operator under the operator’s chosen configuration.'
        ]
      },
      {
        title: '6. Storage, retention, and security',
        paragraphs: [
          'Account and submission records are retained for as long as needed for the educational program, platform administration, dispute resolution, security, or applicable legal obligations. Browser-stored preferences and session data remain until they expire, you sign out, or you clear site data.',
          'Reasonable technical and organizational safeguards are used, including authenticated access and role restrictions. No online service can guarantee absolute security, so users must protect their credentials and promptly report suspected unauthorized access.'
        ]
      },
      {
        title: '7. Sharing and international processing',
        paragraphs: [
          'Information is available to authorized personnel according to their role, such as instructors reviewing student submissions. It may also be shared with contracted technology providers or authorities when required by law.',
          'If a configured provider processes information outside Uzbekistan, the operating organization must assess and implement any consent, localization, contractual, or cross-border-transfer safeguards required by applicable law.'
        ]
      },
      {
        title: '8. Your choices and rights',
        bullets: [
          'Request access to or correction of your personal information.',
          'Request deletion, restriction, or anonymization where applicable.',
          'Withdraw consent where consent is the legal basis, without affecting earlier lawful processing.',
          'Ask how your information is used or object to processing where applicable.',
          'Complain to the competent authority or seek another remedy available under applicable law.'
        ],
        paragraphs: ['Submit requests to the administrator or privacy contact designated by the organization operating your MedSim deployment. Identity verification may be required before a request is completed.']
      },
      {
        title: '9. Changes and contact',
        paragraphs: [
          'This policy may be updated when the platform, provider configuration, or legal requirements change. The current version and update date will be posted here.',
          'For privacy questions or requests, contact the administrator or designated data-protection contact at the university or organization that provided your MedSim account.'
        ]
      }
    ]
  },
  terms: {
    eyebrow: 'Platform rules',
    title: 'Terms of Use',
    icon: FiFileText,
    intro: 'These terms govern access to and use of MedSim. By creating an account or using the platform, you agree to them.',
    sections: [
      {
        title: '1. The platform',
        paragraphs: ['MedSim is an educational clinical-simulation system. It lets learners interview fictional AI patients, request simulated findings, submit diagnoses and management plans, receive automated feedback, and, where enabled, receive instructor review.']
      },
      {
        title: '2. Educational use only',
        paragraphs: [
          'MedSim is not a medical device, clinical decision-support service, emergency service, or substitute for professional supervision. Its cases and AI outputs may be incomplete or incorrect and must not be used to diagnose or treat a real person.',
          'If you need medical care or emergency assistance, contact an appropriate licensed professional or local emergency service. Scores and AI feedback are educational aids; the operating institution decides how, if at all, they affect formal assessment.'
        ]
      },
      {
        title: '3. Accounts and access',
        bullets: [
          'Provide accurate registration information and keep it current.',
          'Keep your password, authentication token, and instructor invite code confidential.',
          'Use only the account and role assigned or authorized for you.',
          'Notify the platform administrator promptly if you suspect unauthorized access.',
          'You are responsible for activity performed through your account unless applicable law provides otherwise.'
        ]
      },
      {
        title: '4. Acceptable use',
        paragraphs: ['You may use MedSim only for lawful education, teaching, review, research, or administration authorized by the operating organization. You must not:'],
        bullets: [
          'Enter identifiable real-patient data or other information you are not authorized to disclose.',
          'Attempt to access another user’s account, private submission, or restricted instructor function.',
          'Probe, disrupt, overload, reverse engineer, scrape, or bypass platform safeguards except where expressly permitted by law and written authorization.',
          'Use the platform to deceive, harass, discriminate, infringe rights, or generate unlawful content.',
          'Present AI-generated output as verified medical advice or use it in real clinical care.'
        ]
      },
      {
        title: '5. AI-generated content and assessment',
        paragraphs: [
          'AI responses are probabilistic. Similar answers may produce different wording or results. The evaluator is instructed to grade overall clinical meaning rather than exact word matching, but automated scoring can still make mistakes.',
          'Users should critically review AI output. Instructors and administrators may review transcripts and override or supplement automated feedback according to their institution’s assessment rules.'
        ]
      },
      {
        title: '6. Content and intellectual property',
        paragraphs: [
          'The platform software, design, branding, and supplied case materials are protected by applicable intellectual-property law. Subject to these terms, you receive a limited, non-exclusive, non-transferable right to use MedSim for authorized purposes.',
          'You retain any rights you hold in original content you submit. You authorize the operator and its service providers to store and process that content as needed to operate, secure, assess, and improve the service, subject to the Privacy Policy and applicable institutional rules.'
        ]
      },
      {
        title: '7. Availability, suspension, and termination',
        paragraphs: [
          'The platform may be changed, interrupted, or unavailable for maintenance, security, provider outages, budget limits, or circumstances beyond reasonable control. Access may be restricted or terminated for misuse, security risk, legal requirements, or the end of your relationship with the operating organization.'
        ]
      },
      {
        title: '8. Disclaimers and liability',
        paragraphs: [
          'To the extent permitted by law, MedSim is provided “as is” and “as available,” without a guarantee that every case, AI response, score, translation, or service component will be accurate, complete, uninterrupted, or suitable for a particular purpose.',
          'Nothing in these terms excludes liability that cannot lawfully be excluded. Subject to that limitation, the operator is not responsible for harm caused by prohibited real-world clinical reliance, user-supplied inaccurate information, credential sharing, third-party outages, or unauthorized use.'
        ]
      },
      {
        title: '9. Privacy, changes, and governing rules',
        paragraphs: [
          'Personal information is handled under the Privacy Policy and browser storage is described in the Cookie Policy. The operating organization may provide additional institutional rules that also apply.',
          'These terms may be updated with reasonable notice when material changes are made. Applicable law and the operating organization’s agreement with you govern these terms. Disputes should first be raised with that organization’s administrator and may then be handled through the competent authority or court.'
        ]
      },
      {
        title: '10. Contact',
        paragraphs: ['For account, technical, legal, or educational-use questions, contact the administrator or support contact designated by the university or organization that provided access to MedSim.']
      }
    ]
  },
  cookies: {
    eyebrow: 'Browser storage',
    title: 'Cookie Policy',
    icon: FiDatabase,
    intro: 'This policy explains how MedSim uses browser storage technologies to keep sessions working and remember preferences.',
    sections: [
      {
        title: '1. Cookies and similar technologies',
        paragraphs: [
          'Cookies are small files a website may place on your device. MedSim’s current web application primarily uses localStorage and sessionStorage—browser features that store small pieces of data on your device—instead of advertising cookies.',
          'These technologies may contain personal data when they store account or authentication information, so they are covered by the Privacy Policy and applicable data-protection law.'
        ]
      },
      {
        title: '2. Storage MedSim uses',
        bullets: [
          'Authentication storage: an access token and basic account details keep you signed in. Choosing “Remember me” uses persistent local storage; otherwise session storage is used until the browser session ends.',
          'Preference storage: the selected language and interface preferences help restore your chosen experience.',
          'Training continuity storage: local case caches and transcript backups can help restore recent information and completed-case indicators.',
          'Security and operational data: the server may process request and usage logs needed to authenticate, protect, troubleshoot, and operate the platform.'
        ]
      },
      {
        title: '3. Analytics and advertising',
        paragraphs: ['The default MedSim application does not set advertising cookies and does not include optional browser-based analytics cookies. If an operating organization adds analytics or other optional technologies, it must provide an updated notice and any consent controls required by applicable law.']
      },
      {
        title: '4. How long data remains',
        bullets: [
          'Session storage is normally removed when the relevant browser session ends.',
          'Persistent local storage remains until the application removes it, you sign out where applicable, or you clear the site’s browser data.',
          'Clearing browser storage does not automatically delete submissions already saved on the MedSim server.'
        ]
      },
      {
        title: '5. Your controls',
        paragraphs: [
          'You can clear or block site data through your browser settings. Blocking necessary storage may prevent sign-in, language preferences, local backups, and other core features from working correctly.',
          'You can choose not to use “Remember me” so authentication information is stored only for the browser session. Signing out removes MedSim authentication information from both local and session storage.'
        ]
      },
      {
        title: '6. Changes and contact',
        paragraphs: [
          'This policy will be updated if MedSim begins using materially different browser-storage technologies.',
          'For questions about browser storage, contact the administrator or privacy contact designated by the university or organization operating your MedSim deployment.'
        ]
      }
    ]
  }
};

export default function LegalPage({ documentType }) {
  const navigate = useNavigate();
  const policy = policies[documentType] || policies.privacy;
  const Icon = policy.icon;

  return (
    <div className="legal-page">
      <header className="legal-header">
        <Link className="legal-brand" to="/" aria-label="MedSim home">
          <span>⚕</span>
          <strong>MedSim</strong>
        </Link>
        <nav aria-label="Legal documents">
          <Link className={documentType === 'terms' ? 'active' : ''} to="/legal/terms">Terms</Link>
          <Link className={documentType === 'privacy' ? 'active' : ''} to="/legal/privacy">Privacy</Link>
          <Link className={documentType === 'cookies' ? 'active' : ''} to="/legal/cookies">Cookies</Link>
        </nav>
      </header>

      <main className="legal-shell">
        <button type="button" className="legal-back" onClick={() => navigate(-1)}>
          <FiArrowLeft /> Back
        </button>

        <section className="legal-hero">
          <div className="legal-icon"><Icon /></div>
          <div>
            <p className="legal-eyebrow">{policy.eyebrow}</p>
            <h1>{policy.title}</h1>
            <p>{policy.intro}</p>
            <span>MedSim · Version for the Republic of Uzbekistan · Last updated {LAST_UPDATED}</span>
          </div>
        </section>

        <div className="legal-layout">
          <aside className="legal-toc" aria-label="On this page">
            <strong>On this page</strong>
            {policy.sections.map((section, index) => (
              <a key={section.title} href={`#section-${index + 1}`}>{section.title.replace(/^\d+\.\s*/, '')}</a>
            ))}
          </aside>

          <article className="legal-document">
            <div className="legal-note">
              This page describes the default MedSim application. Your institution may provide additional terms or a deployment-specific privacy notice.
            </div>
            {policy.sections.map((section, index) => (
              <section id={`section-${index + 1}`} key={section.title}>
                <h2>{section.title}</h2>
                {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                {section.bullets && <ul>{section.bullets.map((item) => <li key={item}>{item}</li>)}</ul>}
              </section>
            ))}
          </article>
        </div>
      </main>

      <footer className="legal-footer">
        <span>© 2026 MedSim Clinical Simulator</span>
        <span>Educational use only</span>
      </footer>
    </div>
  );
}
